/*
 * Only the newest lookup may fill a list, and (optionally) only the last keystroke's lookup is sent.
 *
 * A typeahead fires a request per keystroke and the answers come back in whatever order the server
 * finishes them, so the list a few hundred milliseconds later can be the answer to a query the user
 * has already typed past. The job search makes this severe rather than merely untidy:
 * `searchFields` stores every prefix of every word (see routes/api/lib/searchify.js), so "c" matches
 * a page of unrelated jobs while "crystal" matches one - and when the "c" answer arrived late it
 * replaced the job the user was about to click, which is #683.
 *
 *   const lookup = latestQuery(300)
 *   lookup(() => this.findJob(value), jobs => { this.matches = jobs || [] })
 *   lookup.cancel()          // the user chose something; drop anything in flight or pending
 *
 * The lookup takes a *function* rather than a promise so the delay can be applied before the
 * request is made, not after. Debouncing the other way round - holding back the input event - holds
 * back the caller's model too, and a brand typed and saved inside that delay was saved empty.
 *
 * `cancel` also stops a lookup that is still waiting out the delay, so choosing an item cannot be
 * followed by a dropdown opening over the field.
 *
 * Zero milliseconds means "send now, but still keep only the newest answer" - which is what the
 * lookups that are already in memory want.
 */
export const latestQuery = (debounceMs = 0) => {
  let latest = 0
  let pending

  const run = (fire, apply) => {
    const mine = ++latest
    clearTimeout(pending)

    const go = () => {
      pending = undefined
      Promise.resolve()
        .then(fire)
        .then(result => {
          if (mine === latest) apply(result)
        })
        .catch(err => {
          // A stale failure is not worth reporting: the user's current query is the one that matters.
          if (mine === latest) console.log('typeahead lookup failed', err)
        })
    }

    if (debounceMs) {
      pending = setTimeout(go, debounceMs)
    } else {
      go()
    }

    return mine
  }

  run.cancel = () => {
    latest++
    clearTimeout(pending)
    pending = undefined
  }

  return run
}
